import Button from '@/components/Button/Button';
import React, { useMemo, useState } from 'react';
import CircleNumber from '@/components/CircleNumber/CircleNumber';
import { createSequence, editSequenceSteps } from '@/services/sequencesService';
import { sendAnalytics } from '@/services/analyticsService';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import toast from 'react-hot-toast';
import Tooltip from '@/components/Tooltip/Tooltip';
import { useSequences } from '../hooks/useSequences';
import { useSequencesContext } from '../SequencesContext';

export default function NewSequenceFooter() {
	const {
		sequencePage,
		setSequencePage,
		selectedTemplate,
		setShowSendAll,
		selectedBusinesses,
		selectedSequence,
		selectedLists,
		setSelectedSequence
	} = useSequencesContext();

	const [user, monthlyLimit] = useSelector(
		(state: RootState) => [
			state.user.getIn(['authorize', 'user']).toJS(),
			state.email.getIn(['email', 'monthlyLimit'])
		],
		shallowEqual
	);

	const { fetchSequences } = useSequences();

	const [draftStatus, setDraftStatus] = useState<'IDLE' | 'SAVING' | 'SAVED'>(
		'IDLE'
	);

	function handleGoBackSequences() {
		setSequencePage('MY-SEQUENCES');
	}

	function handleSkipButton() {
		setSequencePage('MY-SEQUENCES');
	}

	function handleGoToStepsCreation() {
		if (!selectedSequence || !selectedTemplate) return;
		setSequencePage('STEPS-CREATION');
		setSelectedSequence({
			...selectedSequence,
			steps: selectedTemplate.steps
		});

		sendAnalytics({
			type: 'track',
			userId: user.email,
			templateId: selectedTemplate?.id,
			eventName: 'user_selected_a_sequence_template'
		});
	}

	function handleSaveDraft() {
		if (!selectedSequence) return;

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: selectedSequence.id,
			eventName: 'user_saved_sequence_as_draft'
		});

		// If the sequence doesnt have an id that means it is a new sequence, so it needs to be created
		if (!selectedSequence.id) {
			setDraftStatus('SAVING');
			createSequence({
				name: selectedSequence.name,
				steps: selectedSequence.steps,
				status: 'DRAFT'
			})
				.then(res => {
					if (res.status !== 200) {
						throw new Error(
							'Error saving draft! Please try again later.'
						);
					}
					toast.success('Draft saved successfully!');
					setDraftStatus('SAVED');
					fetchSequences();
					setTimeout(() => {
						setDraftStatus('IDLE');
						setSequencePage('MY-SEQUENCES');
						setSelectedSequence(undefined);
					}, 1000);
				})
				.catch(err => {
					setDraftStatus('IDLE');
					toast.error(err.message);
				});
			return;
		}

		const stepsWithoutId = selectedSequence.steps.map(step =>
			step.justCreated
				? {
						body: step.body,
						subject: step.subject,
						sendAfterInterval: step.sendAfterInterval,
						sendAfterType: step.sendAfterType,
						order: step.order
					}
				: step
		);

		setDraftStatus('SAVING');
		editSequenceSteps({
			campaignId: selectedSequence.id,
			steps: stepsWithoutId
		})
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error saving sequence! Please try again later.'
					);
				}
				toast.success('Draft saved successfully!');
				setDraftStatus('SAVED');
				fetchSequences();
				setTimeout(() => {
					setDraftStatus('IDLE');
					setSequencePage('MY-SEQUENCES');
					setSelectedSequence(undefined);
				}, 1000);
			})
			.catch(err => {
				setDraftStatus('IDLE');
				toast.error(err.message);
			});
	}

	function handleNextStep() {
		if (!selectedSequence) return;

		if (!selectedSequence.id) {
			createSequence({
				name: selectedSequence.name,
				steps: selectedSequence.steps,
				status: 'DRAFT'
			})
				.then(res => {
					setSelectedSequence({
						...selectedSequence,
						id: res.data?.data.id,
						justCreated: true
					});
					fetchSequences();
				})
				.catch(() => {
					console.log('error');
				});
		}

		setSequencePage('ADD-BUSINESS');
	}

	function handleOpenSendAll() {
		setShowSendAll(true);
	}

	const stripHtml = (html: string) => {
		const tmp = document.createElement('DIV');
		tmp.innerHTML = html;
		return tmp.textContent || tmp.innerText || '';
	};

	const businessesInSelectedListsCount = selectedLists.reduce(
		(count, list) => count + list.businessIds.length,
		0
	);

	const willSequenceFail = businessesInSelectedListsCount > monthlyLimit;

	const isReviewButtonDisabled = useMemo(() => {
		const areBusinessesSelected = selectedBusinesses.length > 0;
		return !areBusinessesSelected;
	}, [selectedBusinesses]);

	const [isNextButtonDisabled, errorMessage] = useMemo(() => {
		const steps = selectedSequence?.steps || [];

		// A Sequence must have at least two steps
		const hasAtLeastTwoSteps = steps.length >= 2;
		// Each step must have a subject and body
		const hasValidSteps = steps.every(
			step => step.subject && stripHtml(step.body).trim() !== ''
		);
		// AI Snippet can only be placed in the first step
		const hasAISnippetAfterFirstStep = steps.some(
			step => step.body.includes('[ai_snippet]') && step.order > 0
		);
		// The body of each step must not contain duplicate AI Snippets
		const hasDuplicateAISnippet = steps.some(
			step => (step.body.match(/\[ai_snippet\]/g) || []).length > 1
		);
		// The body must not contain brackets information other than [business_name] or [ai_snippet]
		const invalidBracket = steps
			.map(step => {
				const bodyAndSubject = `${step.body} ${step.subject}`;
				const matches = bodyAndSubject.match(/\[.*?\]/g) || [];
				const invalidBrackets = matches.filter(
					match =>
						match !== '[business_name]' && match !== '[ai_snippet]'
				);
				return invalidBrackets.length > 0 ? invalidBrackets[0] : null;
			})
			.find(match => match);
		// The body and suject must not contain braces
		const invalidBrace = steps
			.map(step => {
				const bodyAndSubject = `${step.body} ${step.subject}`;
				const matches = bodyAndSubject.match(/\{.*?\}/g) || [];
				return matches.length > 0 ? matches[0] : null;
			})
			.find(match => match);

		const error =
			(!hasAtLeastTwoSteps &&
				'Your sequence must have at least two steps.') ||
			(!hasValidSteps && 'Each step must have a subject and body.') ||
			(hasAISnippetAfterFirstStep &&
				'AI Snippet can only be placed in the first step.') ||
			(hasDuplicateAISnippet &&
				'The body must contain only one AI Snippet.') ||
			(invalidBrace &&
				`Replace text in braces with the appropriate info: ${invalidBrace}`) ||
			(invalidBracket &&
				`You have a invalid custom text: ${invalidBracket}`);

		return [
			!hasAtLeastTwoSteps ||
				!hasValidSteps ||
				hasAISnippetAfterFirstStep ||
				hasDuplicateAISnippet ||
				invalidBrace !== undefined ||
				invalidBracket !== undefined,
			error
		];
	}, [selectedSequence]);

	const warningString = `The lists you've selected have a total of ${businessesInSelectedListsCount} businesses, and you can schedule a max of ${monthlyLimit} businesses`;

	return (
		<div className="absolute bottom-0 left-0 flex h-16 w-full items-center justify-between border-t px-8">
			<button
				className="flex items-center gap-3 text-orange"
				onClick={handleGoBackSequences}
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					strokeWidth={1.5}
					stroke="currentColor"
					className="h-5 w-5"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
					/>
				</svg>
				<span>Back to My sequences</span>
			</button>
			<div className="h-12 w-2/3">
				<SequenceProgressSteps
					isAddBusinessDisabled={isNextButtonDisabled}
					isStepsCreationDisabled={selectedTemplate === undefined}
				/>
			</div>
			<div className="h-10 w-64">
				{sequencePage === 'SEQUENCE-START' && (
					<Button
						onClick={handleGoToStepsCreation}
						disabled={selectedTemplate === undefined}
					>
						Select and continue
					</Button>
				)}
				{sequencePage === 'STEPS-CREATION' && (
					<div className="relative flex h-full items-center gap-5">
						{errorMessage && (
							<div className="absolute -left-8">
								<Tooltip
									content={errorMessage || ''}
									longerBubble
									position="top"
								>
									<div className="flex h-5 w-5 items-center justify-center rounded-full border border-yellow-600 text-yellow-600">
										!
									</div>
								</Tooltip>
							</div>
						)}
						<Button
							disabled={
								draftStatus !== 'IDLE' || isNextButtonDisabled
							}
							onClick={handleSaveDraft}
							variant="secondary"
						>
							{draftStatus === 'IDLE' &&
								(selectedSequence?.id ? 'Save' : 'Save Draft')}
							{draftStatus === 'SAVING' && 'Saving...'}
							{draftStatus === 'SAVED' && 'Saved'}
						</Button>
						{/* If the Sequence is being edited it should no have ne next button */}
						{selectedSequence?.status === 'DRAFT' && (
							<Button
								disabled={isNextButtonDisabled}
								onClick={handleNextStep}
							>
								Next
							</Button>
						)}
					</div>
				)}
				{sequencePage === 'ADD-BUSINESS' && (
					<div className="flex h-full gap-5">
						<Button variant="secondary" onClick={handleSkipButton}>
							Skip
						</Button>
						<Button
							disabled={isReviewButtonDisabled}
							onClick={() => {
								willSequenceFail
									? toast.error(warningString)
									: handleOpenSendAll();
							}}
						>
							Review
						</Button>
					</div>
				)}
			</div>
		</div>
	);
}

function SequenceProgressSteps({
	isAddBusinessDisabled,
	isStepsCreationDisabled
}: {
	isAddBusinessDisabled: boolean;
	isStepsCreationDisabled: boolean;
}) {
	const { sequencePage, setSequencePage, selectedSequence } =
		useSequencesContext();

	const pagesOptions = {
		'MY-SEQUENCES': 0,
		'SEQUENCE-START': 1,
		'STEPS-CREATION': 2,
		'ADD-BUSINESS': 3
	};

	const currentNumber = pagesOptions[sequencePage];
	const selectedSequenceExists = selectedSequence?.id !== undefined;
	const shouldHideAddBusiness =
		selectedSequenceExists &&
		!selectedSequence.justCreated &&
		selectedSequence?.status !== 'DRAFT';

	return (
		<div
			className={`mx-auto flex h-full w-full select-none overflow-hidden rounded-2xl border border-dark-grey text-center ${shouldHideAddBusiness ? 'max-w-2xl' : 'max-w-4xl'} `}
		>
			<button
				onClick={() => setSequencePage('SEQUENCE-START')}
				className="relative m-auto flex h-full w-full cursor-pointer items-center justify-center gap-2"
			>
				<div className="h-5 w-5">
					<CircleNumber
						number={1}
						variant={currentNumber === 1 ? 'filled' : 'outlined'}
					/>
				</div>
				<span
					className={`text-lg ${
						currentNumber === 1 ? 'text-orange' : 'text-gray-200'
					}`}
				>
					Sequence start
				</span>
				<div
					className={`absolute bottom-0 right-0 top-0 my-auto h-9 w-9 border-r border-t ${
						currentNumber >= 1 && 'border-orange'
					} `}
					style={{
						rotate: '45deg'
					}}
				/>
			</button>
			<button
				onClick={() => setSequencePage('STEPS-CREATION')}
				disabled={isStepsCreationDisabled}
				className="relative m-auto flex h-full w-full cursor-pointer items-center justify-center gap-2"
			>
				<div className="h-5 w-5">
					<CircleNumber
						number={2}
						variant={
							currentNumber < 2
								? 'gray'
								: currentNumber === 2
									? 'filled'
									: 'outlined'
						}
					/>
				</div>
				<span
					className={`text-lg ${
						currentNumber < 2
							? ''
							: currentNumber === 2
								? 'text-orange'
								: 'text-gray-200'
					}`}
				>
					Steps creation
				</span>
				<div
					className={`absolute bottom-0 right-0 top-0 my-auto h-9 w-9 ${
						!shouldHideAddBusiness && 'border-r border-t'
					} ${currentNumber >= 2 && 'border-orange'}`}
					style={{
						rotate: '45deg'
					}}
				/>
			</button>
			{!shouldHideAddBusiness && (
				<button
					onClick={() => setSequencePage('ADD-BUSINESS')}
					disabled={isAddBusinessDisabled}
					className="m-auto flex h-full w-full cursor-pointer items-center justify-center gap-2"
				>
					<div className="h-5 w-5">
						<CircleNumber
							number={3}
							variant={
								sequencePage === 'ADD-BUSINESS'
									? 'filled'
									: 'gray'
							}
						/>
					</div>
					<span
						className={`text-lg ${
							sequencePage === 'ADD-BUSINESS' && 'text-orange'
						}`}
					>
						Add business
					</span>
				</button>
			)}
		</div>
	);
}
